/**
 * DeliveryProofPanel — khối "Ảnh chụp cùng khách" của đơn ảnh thẻ.
 *
 * Tên cũ "Ảnh bàn giao cho khách" đổi ngày 07/10/2026 (CEO chốt 3a): nhân viên
 * đọc tên đó tưởng là chỗ xem ẢNH ĐÃ GỬI KHÁCH, trong khi khối chỉ giữ ảnh thợ
 * chụp cùng khách lúc check-in và lúc giao sản phẩm in. Ảnh đã gửi khách xem ở
 * khối "Ảnh gốc" (ô "Đã duyệt — đã giao"). Ở chi tiết đơn khối thu còn MỘT
 * dòng — tên, số ảnh, nút tải — bấm "Xem ảnh" mới bày lưới.
 *
 * CEO chốt 29/09/2026 (1a · 2b · 3a · 4): thợ chụp có chỗ tải ảnh check-in cùng
 * khách và ảnh bàn giao sản phẩm in NGAY từ lúc được xếp ca, tách khỏi nút
 * "Đã giao". Tải ảnh KHÔNG đổi trạng thái đơn. Không bắt buộc với đơn khách đã
 * ký nghiệm thu (QĐ 0107 giữ nguyên) — chỉ nhắc bằng dòng vàng khi còn trống.
 *
 * Người gọi quyết định có vẽ khối hay không (`canAddDeliveryProof` ở chi tiết
 * đơn, cờ `isPhoto` của máy chủ ở thẻ ca). Máy chủ soát quyền lần nữa.
 */
import { useState } from "react";
import { Camera, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/ui/button";
import { PhotoThumbList } from "@/components/PhotoThumb";
import { useAuthV2 } from "@/lib/authv2/store";
import { ProofCaptureSheet } from "./ProofCaptureSheet";
import { useOrderProofs } from "./api";
import { canAddDeliveryProof, type DeliveryProofOrder } from "./deliveryProofAccess";
import { proofsOfKind, type OrderProof } from "./utils";
import { HintNote } from "@/components/HintTip";

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function HandoverGrid({ items }: { items: OrderProof[] }) {
  const anh = items.flatMap((p) => p.images.map((src) => ({ src, proof: p })));
  return (
    <PhotoThumbList
      images={anh.map((a) => a.src)}
      label="Ảnh chụp cùng khách đã tải"
      alt={(i) => `Ảnh chụp cùng khách ${i + 1}`}
      openLabel={(i) => `Xem lớn ảnh chụp cùng khách ${i + 1}`}
      caption={(_, i) => {
        const p = anh[i]!.proof;
        return (
          <div className="mt-0.5 max-w-[240px] truncate text-xs text-muted-foreground">
            {p.takenByName} · {fmtTime(p.createdAt)}
            {p.note ? ` · ${p.note}` : ""}
          </div>
        );
      }}
    />
  );
}

interface Props {
  orderId: string;
  /**
   * Thẻ ca ở trang chủ: bỏ lưới ảnh, chỉ ghi số ảnh đã có — thẻ ca đã chật,
   * lưới ảnh lớn nằm ở chi tiết đơn.
   */
  compact?: boolean;
}

export function DeliveryProofPanel({ orderId, compact = false }: Props) {
  const [open, setOpen] = useState(false);
  /** Lưới ảnh ở chi tiết đơn: thu gọn sẵn, bấm "Xem ảnh" mới bày (CEO chốt 3a). */
  const [expanded, setExpanded] = useState(false);
  const q = useOrderProofs(orderId);
  const delivery = proofsOfKind(q.data ?? [], "delivery");
  const photoCount = delivery.reduce((n, p) => n + p.images.length, 0);
  const has = !q.isError && !q.isPending && delivery.length > 0;

  const uploadButton = (
    <Button
      type="button"
      size="sm"
      variant="outline"
      aria-label="Tải ảnh chụp cùng khách"
      onClick={() => setOpen(true)}
    >
      <Camera className="size-4" aria-hidden />
      Tải ảnh
    </Button>
  );

  // Dòng tình trạng: chữ vàng khi còn trống, xanh khi đã có — cùng màu cũ.
  const status = q.isError ? (
    <p role="alert" className="text-xs text-[var(--color-danger-text)]">
      {q.error instanceof Error ? q.error.message : "Không tải được ảnh chụp cùng khách của đơn"}
    </p>
  ) : q.isPending ? null : delivery.length === 0 ? (
    <p className="text-xs font-semibold text-[var(--color-warning-text)]">Chưa có ảnh nào</p>
  ) : (
    <p className="text-xs font-semibold text-[var(--color-success-text)]">Đã có {photoCount} ảnh</p>
  );

  const sheet = (
    <ProofCaptureSheet orderId={orderId} kind="delivery" withNote open={open} onOpenChange={setOpen} />
  );

  if (compact) {
    return (
      <section
        aria-label="Ảnh chụp cùng khách"
        className="mt-2.5 space-y-1.5 rounded-md border border-border bg-surface p-2.5"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-bold text-foreground">Ảnh chụp cùng khách</p>
            <HintNote>Ảnh check-in cùng khách và ảnh lúc giao sản phẩm in.</HintNote>
          </div>
          {uploadButton}
        </div>
        {status}
        {sheet}
      </section>
    );
  }

  return (
    <section aria-label="Ảnh chụp cùng khách" className="space-y-2 rounded-md border bg-surface px-3 py-2">
      {/* MỘT dòng: tên · tình trạng · nút. Điện thoại: tên + nút một hàng,
          tình trạng xuống hàng dưới — bóp chung một hàng là vỡ từng chữ. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p
          className="flex flex-1 items-center gap-1.5 text-sm font-semibold sm:flex-none"
          title="Ảnh check-in cùng khách và ảnh lúc giao sản phẩm in"
        >
          <Camera className="size-4 text-muted-foreground" aria-hidden />
          Ảnh chụp cùng khách
        </p>
        <div className="order-last w-full min-w-0 sm:order-none sm:w-auto sm:flex-1">{status}</div>
        <div className="flex items-center gap-2">
          {has && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? <ChevronUp className="size-4" aria-hidden /> : <ChevronDown className="size-4" aria-hidden />}
              {expanded ? "Ẩn ảnh" : "Xem ảnh"}
            </Button>
          )}
          {uploadButton}
        </div>
      </div>
      {has && expanded && (
        <>
          <HintNote>Ảnh check-in cùng khách và ảnh lúc giao sản phẩm in.</HintNote>
          <HandoverGrid items={delivery} />
        </>
      )}
      {sheet}
    </section>
  );
}

/**
 * Chi tiết đơn (thẻ "Việc thợ chụp", bản máy tính lẫn điện thoại): tự soát
 * quyền rồi mới vẽ khối — ai không được tải thì xem ảnh ở mục "Bằng chứng"
 * của thẻ Ekip như cũ.
 */
export function OrderDeliveryProofSection({
  order,
}: {
  order: DeliveryProofOrder & { id: string };
}) {
  const me = useAuthV2((s) => s.currentStaff);
  const user = me ? { role: me.role ?? undefined, department: me.department ?? undefined } : null;
  if (!canAddDeliveryProof(user, me?.id, order)) return null;
  return <DeliveryProofPanel orderId={order.id} />;
}
